// src/components/admin/UserManagement.tsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { 
  collection, 
  query, 
  getDocs, 
  doc,
  setDoc,
  getDoc,
  updateDoc,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../../firebase';
import Navbar from '../layout/Navbar';
import AdminNav from './AdminNav';
import CsvImporter from '../../components/admin/CsvImporter';
interface UserData {
  uid: string;
  email: string;
  displayName: string;
  password?: string;
  role: 'admin' | 'student';
  createdAt: any;
  lastLogin?: any;
}

const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<UserData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showImporter, setShowImporter] = useState(false);
  
  const { currentUser } = useAuth();
  
  // Check if user is admin based on our users collection
  const [isAdmin, setIsAdmin] = useState(false);
  
  // Check admin status
  useEffect(() => {
    const checkAdmin = async () => {
      if (!currentUser) return;
      
      try {
        // Get user doc from users collection
        const userRef = doc(db, 'users', currentUser.uid);
        const userDoc = await getDoc(userRef);
        
        if (userDoc.exists() && userDoc.data().role === 'admin') {
          setIsAdmin(true);
        } else {
          // If this is a first-time setup, let's check for admin by email
          const isAdminEmail = 
            currentUser.email === 'jhatcher4@ccc.edu' || 
            currentUser.email === 'admin@example.com';
          
          if (isAdminEmail) {
            // Create user in users collection as admin
            await setDoc(userRef, {
              uid: currentUser.uid,
              email: currentUser.email,
              displayName: currentUser.displayName || currentUser.email?.split('@')[0] || 'Admin',
              role: 'admin',
              createdAt: serverTimestamp(),
              lastLogin: serverTimestamp()
            });
            setIsAdmin(true);
          }
        }
      } catch (err) {
        console.error("Error checking admin status:", err);
      }
    };
    
    checkAdmin();
  }, [currentUser]);
  
  // Load users from Firestore
  useEffect(() => {
    if (!isAdmin) return;
    
    const fetchUsers = async () => {
      try {
        setLoading(true);
        
        // Get users from our users collection
        const usersRef = collection(db, 'users');
        const querySnapshot = await getDocs(usersRef);
        
        const usersData: UserData[] = [];
        querySnapshot.forEach((doc) => {
          usersData.push(doc.data() as UserData);
        });
        
        setUsers(usersData);
      } catch (err) {
        console.error("Error fetching users:", err);
        setError("Failed to load users");
      } finally {
        setLoading(false);
      }
    };
    
    fetchUsers();
  }, [isAdmin]);
  
  // Toggle user role between 'admin' and 'student'
  const toggleUserRole = async (userId: string, currentRole: 'admin' | 'student') => {
    try {
      const newRole = currentRole === 'admin' ? 'student' : 'admin';
      
      // Update in Firestore
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        role: newRole
      });
      
      // Update in state
      setUsers(prev => prev.map(user => 
        user.uid === userId ? { ...user, role: newRole } : user
      ));
      
      setSuccessMessage(`User role updated to ${newRole}`);
      setError(null);
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
      
    } catch (err) {
      console.error("Error updating role:", err);
      setError("Failed to update role: " + (err as Error).message);
    }
  };
  
  // Export users list as CSV
  const exportUsersCsv = () => {
    // Create CSV content
    const csvContent = [
      // Header row
      ["UID", "Email", "Name", "Password", "Role"].join(","),
      // Data rows
      ...users.map(user => [
        user.uid,
        user.email,
        user.displayName,
        user.password || "N/A",
        user.role
      ].join(","))
    ].join("\n");
    
    // Create download link
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `users_${new Date().toISOString()}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  // Filter users based on search term
  const filteredUsers = users.filter(user => 
    user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (user.displayName && user.displayName.toLowerCase().includes(searchTerm.toLowerCase()))
  );
  
  // Refresh user list after import
  const refreshUsers = async () => {
    try {
      setLoading(true);
      
      // Get users from our users collection
      const usersRef = collection(db, 'users');
      const querySnapshot = await getDocs(usersRef);
      
      const usersData: UserData[] = [];
      querySnapshot.forEach((doc) => {
        usersData.push(doc.data() as UserData);
      });
      
      setUsers(usersData);
      setSuccessMessage("User list refreshed");
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
    } catch (err) {
      console.error("Error refreshing users:", err);
      setError("Failed to refresh users");
    } finally {
      setLoading(false);
    }
  };
  
  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gray-100">
        <Navbar />
        <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
          <div className="px-4 py-6 sm:px-0">
            <div className="bg-white shadow overflow-hidden sm:rounded-lg p-6">
              <h1 className="text-xl font-bold text-red-600">Access Denied</h1>
              <p className="mt-2">Only administrators can access this page.</p>
            </div>
          </div>
        </main>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-100">
      <Navbar />
      <AdminNav />
      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 sm:px-0">
          <h1 className="text-2xl font-semibold text-gray-900">User Management</h1>
          <p className="mt-1 text-sm text-gray-600">
            View and manage user accounts and roles.
          </p>
        </div>
        
        {error && (
          <div className="mt-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
            <span className="block sm:inline">{error}</span>
            <button 
              onClick={() => setError(null)} 
              className="float-right font-bold"
            >
              ×
            </button>
          </div>
        )}
        
        {successMessage && (
          <div className="mt-4 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded">
            <span className="block sm:inline">{successMessage}</span>
          </div>
        )}
        
        {/* CSV Importer */}
        <div className="mt-6">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-medium text-gray-900">Student Import</h2>
            <button 
              onClick={() => setShowImporter(!showImporter)}
              className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none"
            >
              {showImporter ? 'Hide Importer' : 'Show CSV Importer'}
            </button>
          </div>
          
          {showImporter && (
            <div className="mt-4">
              <CsvImporter />
              <div className="mt-2 flex justify-end">
                <button 
                  onClick={refreshUsers}
                  className="text-indigo-600 hover:text-indigo-900 text-sm"
                >
                  Refresh user list after import
                </button>
              </div>
            </div>
          )}
        </div>
        
        {/* User List */}
        <div className="mt-6 bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-medium text-gray-900">User List</h2>
              <div className="flex space-x-2">
                <div className="relative rounded-md shadow-sm">
                  <input
                    type="text"
                    className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pr-10 sm:text-sm border-gray-300 rounded-md"
                    placeholder="Search users..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                    <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                    </svg>
                  </div>
                </div>
                <button
                  onClick={exportUsersCsv}
                  className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                >
                  Export CSV
                </button>
              </div>
            </div>
            
            {loading ? (
              <div className="py-10 flex justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600"></div>
              </div>
            ) : (
              <div className="mt-4 flex flex-col">
                <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                  <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
                    <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              User
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              UID
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Role
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Password
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {filteredUsers.length === 0 ? (
                            <tr>
                              <td colSpan={5} className="px-6 py-4 text-center text-sm text-gray-500">
                                No users found
                              </td>
                            </tr>
                          ) : (
                            filteredUsers.map(user => (
                              <tr key={user.uid}>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="flex items-center">
                                    <div>
                                      <div className="text-sm font-medium text-gray-900">
                                        {user.displayName || user.email.split('@')[0]}
                                      </div>
                                      <div className="text-sm text-gray-500">
                                        {user.email}
                                      </div>
                                    </div>
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  <span className="font-mono text-xs">{user.uid}</span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                    user.role === 'admin' ? 'bg-purple-100 text-purple-800' : 'bg-green-100 text-green-800'
                                  }`}>
                                    {user.role}
                                  </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {user.password || 'N/A'}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                  <button
                                    onClick={() => toggleUserRole(user.uid, user.role)}
                                    className={user.role === 'admin' ? 'text-red-600 hover:text-red-900' : 'text-indigo-600 hover:text-indigo-900'}
                                  >
                                    {user.role === 'admin' ? 'Make Student' : 'Make Admin'}
                                  </button>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default UserManagement;